import { useMemo, useState } from "react";
import Button from "../../../../shared/components/Button";
import OrganizerDatePicker from "../../../../shared/components/OrganizerDatePicker";
import SearchInput from "../../../../shared/components/SearchInput";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import type { OpenMatchGenderPreference } from "../../shared/types/matches";

type MatchFiltersBarProps = {
  searchValue: string;
  dateValue: string;
  clubValue: string;
  cityValue: string;
  categoryValue: string;
  genderValue: "" | OpenMatchGenderPreference;
  timeFromValue: string;
  timeToValue: string;
  availableOnly: boolean;
  clubOptions: Array<{ value: string; label: string }>;
  cityOptions: string[];
  categoryOptions: Array<{ value: string; label: string }>;
  genderOptions: Array<{ value: OpenMatchGenderPreference; label: string }>;
  onSearchChange: (value: string) => void;
  onDateChange: (value: string) => void;
  onClubChange: (value: string) => void;
  onCityChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onGenderChange: (value: "" | OpenMatchGenderPreference) => void;
  onTimeFromChange: (value: string) => void;
  onTimeToChange: (value: string) => void;
  onAvailableOnlyChange: (value: boolean) => void;
  onClear: () => void;
};

type FilterSelectFieldProps = {
  label: string;
  labelIcon?: string;
  icon: string;
  className?: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
};

type FilterLabelProps = {
  label: string;
  icon: string;
};

const TIME_FILTER_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const totalMinutes = index * 30;
  const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
  const minutes = String(totalMinutes % 60).padStart(2, "0");
  const value = `${hours}:${minutes}`;
  return { value, label: value };
});

function FilterLabel({ label, icon }: FilterLabelProps) {
  return (
    <span className="matches-filter-label">
      <span className="material-symbols-outlined" aria-hidden="true">
        {icon}
      </span>
      {label}
    </span>
  );
}

function TimeSelectField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const selectedOption = TIME_FILTER_OPTIONS.find(
    (option) => option.value === value,
  );
  const emptyLabel = siteTexts.openMatches.filters.anyTime;

  return (
    <div className="matches-time-select">
      <span className="matches-time-range__label">{label}</span>
      <div className="matches-time-select__control">
        <span className="matches-time-select__value">
          {selectedOption?.label || emptyLabel}
        </span>
        <select
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-label={label}
        >
          <option value="">{emptyLabel}</option>
          {TIME_FILTER_OPTIONS.map((option) => (
            <option key={`${label}-${option.value}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span
          className="matches-field__chevron material-symbols-outlined"
          aria-hidden="true"
        >
          expand_more
        </span>
      </div>
    </div>
  );
}

function FilterSelectField({
  label,
  labelIcon,
  icon,
  className = "",
  value,
  onChange,
  options,
}: FilterSelectFieldProps) {
  const selectedOption = options.find((option) => option.value === value);
  const resolvedLabelIcon = labelIcon || icon;

  return (
    <label className={`matches-field matches-field--icon${className ? ` ${className}` : ""}`}>
      <FilterLabel label={label} icon={resolvedLabelIcon} />
      <div className="matches-field__control">
        <span className="matches-field__icon material-symbols-outlined" aria-hidden="true">
          {icon}
        </span>
        <span className="matches-field__value">
          {selectedOption?.label || options[0]?.label || ""}
        </span>
        <select
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-label={label}
        >
          {options.map((option) => (
            <option key={`${label}-${option.value || "all"}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span
          className="matches-field__chevron material-symbols-outlined"
          aria-hidden="true"
        >
          expand_more
        </span>
      </div>
    </label>
  );
}

export default function MatchFiltersBar({
  searchValue,
  dateValue,
  clubValue,
  cityValue,
  categoryValue,
  genderValue,
  timeFromValue,
  timeToValue,
  availableOnly,
  clubOptions,
  cityOptions,
  categoryOptions,
  genderOptions,
  onSearchChange,
  onDateChange,
  onClubChange,
  onCityChange,
  onCategoryChange,
  onGenderChange,
  onTimeFromChange,
  onTimeToChange,
  onAvailableOnlyChange,
  onClear,
}: MatchFiltersBarProps) {
  const texts = siteTexts.openMatches;
  const activeAdvancedFiltersCount = useMemo(
    () =>
      [
        dateValue,
        clubValue,
        cityValue,
        categoryValue,
        genderValue,
        timeFromValue,
        timeToValue,
        availableOnly ? "available" : "",
      ].filter(Boolean).length,
    [
      availableOnly,
      categoryValue,
      clubValue,
      cityValue,
      dateValue,
      genderValue,
      timeFromValue,
      timeToValue,
    ],
  );
  const [filtersOpen, setFiltersOpen] = useState(false);

  return (
    <section className="matches-filters" aria-label={texts.title}>
      <div className="matches-filters__search-row">
        <div className="matches-filters__search">
          <SearchInput
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            onClear={() => onSearchChange("")}
            placeholder={texts.filters.searchPlaceholder}
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          className={`matches-filters__toggle${filtersOpen ? " is-active" : ""}`}
          onClick={() => setFiltersOpen((current) => !current)}
          aria-expanded={filtersOpen}
          aria-controls="matches-advanced-filters"
          aria-label={filtersOpen ? texts.actions.hideFilters : texts.actions.showFilters}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            tune
          </span>
          {activeAdvancedFiltersCount > 0 ? (
            <span className="matches-filters__toggle-count">
              {activeAdvancedFiltersCount}
            </span>
          ) : null}
        </Button>
      </div>

      {filtersOpen ? (
        <div className="matches-filters__panel" id="matches-advanced-filters">
          <div className="matches-filters__controls">
            <div className="matches-field matches-field--date">
              <OrganizerDatePicker
                label={texts.filters.date}
                value={dateValue}
                onChange={onDateChange}
              />
            </div>

            <FilterSelectField
              label={texts.filters.club}
              labelIcon="storefront"
              icon="storefront"
              className="matches-field--wide"
              value={clubValue}
              onChange={onClubChange}
              options={[
                { value: "", label: texts.filters.allClubs },
                ...clubOptions,
              ]}
            />

            <FilterSelectField
              label={texts.filters.city}
              labelIcon="pin_drop"
              icon="location_on"
              className="matches-field--wide"
              value={cityValue}
              onChange={onCityChange}
              options={[
                { value: "", label: texts.filters.allCities },
                ...cityOptions.map((city) => ({ value: city, label: city })),
              ]}
            />

            <FilterSelectField
              label={texts.filters.category}
              labelIcon="workspace_premium"
              icon="workspace_premium"
              className="matches-field--wide"
              value={categoryValue}
              onChange={onCategoryChange}
              options={[
                { value: "", label: texts.filters.allCategories },
                ...categoryOptions,
              ]}
            />

            <FilterSelectField
              label={texts.filters.gender}
              labelIcon="groups"
              icon="groups"
              value={genderValue}
              onChange={(nextValue) =>
                onGenderChange(nextValue as "" | OpenMatchGenderPreference)
              }
              options={[
                { value: "", label: texts.filters.allGenders },
                ...genderOptions,
              ]}
            />

            <div className="matches-field matches-field--time-range">
              <FilterLabel label={texts.filters.time} icon="schedule" />
              <div className="matches-time-range">
                <TimeSelectField
                  label={texts.filters.timeFromShort}
                  value={timeFromValue}
                  onChange={onTimeFromChange}
                />
                <TimeSelectField
                  label={texts.filters.timeToShort}
                  value={timeToValue}
                  onChange={onTimeToChange}
                />
              </div>
            </div>
          </div>

          <div className="matches-filters__actions">
            <label className="matches-checkbox matches-checkbox--filter">
              <input
                type="checkbox"
                checked={availableOnly}
                onChange={(event) => onAvailableOnlyChange(event.target.checked)}
              />
              <span>{texts.filters.availableOnly}</span>
            </label>
            <Button
              type="button"
              variant="ghost"
              className="matches-clear-button"
              onClick={onClear}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                filter_alt_off
              </span>
              {texts.actions.clearFilters}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
